/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Create as CreateIcon,
  Rule as RuleIcon,
  Login as LoginIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Info as InfoIcon,
  Forum as ForumIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  EmojiEvents as TrophyIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import EmailVerificationGuard from './EmailVerificationGuard';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import logoImage from '../assets/images/gd-logo-plain-black.svg';

// Custom icon component for CUBE
const CubeIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    style={{ display: 'inline-block' }}
  >
    <path d="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L6.04,7.5L12,10.85L17.96,7.5L12,4.15M5,15.91L11,19.29V12.58L5,9.21V15.91M19,15.91V9.21L13,12.58V19.29L19,15.91Z" />
  </svg>
);

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [debatesMenuAnchorEl, setDebatesMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const { isAuthenticated, user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  
  // DISABLED: Automatic page translation scanning causes excessive API calls
  // This was translating all pages even when not viewing them
  // Each page should handle its own translations when needed
  // usePageTranslationScanner(true);
  
  // Debug logging - only when language changes
  React.useEffect(() => {
    logger.info('Header: Language changed to', i18n.language);
  }, [i18n.language]);
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleDebatesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDebatesMenuAnchorEl(event.currentTarget);
  };

  const handleDebatesMenuClose = () => {
    setDebatesMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };
  
  return (
    <AppBar position="static" color="default" elevation={1}>
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="Gauntlet Debates Logo"
              sx={{
                display: 'inline-block',
                mr: 1,
                width: 32,
                height: 32,
                filter: 'brightness(0) saturate(100%)',
              }}
            />
            Gauntlet Debates™
          </Typography>
          
          {isMobile ? (
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenuOpen}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                keepMounted
              >
                <EmailVerificationGuard action="create a debate">
                  <MenuItem
                    component={RouterLink}
                    to="/debates/new"
                    onClick={handleMenuClose}
                  >
                    <CreateIcon fontSize="small" sx={{ mr: 1 }} />
                    {t('common:newDebate')}
                  </MenuItem>
                </EmailVerificationGuard>
                <MenuItem
                  component={RouterLink}
                  to="/home"
                  onClick={handleMenuClose}
                >
                  <ForumIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common:yourDebates')}
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/debates/public"
                  onClick={handleMenuClose}
                >
                  <PublicIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common:publicDebates', 'Public Debates')}
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/cube"
                  onClick={handleMenuClose}
                >
                  <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <CubeIcon />
                  </Box>
                  {t('common:cube', 'CUBE')}
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/leaderboards"
                  onClick={handleMenuClose}
                >
                  <TrophyIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common:leaderboards', 'Leaderboards')}
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/rules"
                  onClick={handleMenuClose}
                >
                  <RuleIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common:rules', 'Rules')}
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/about"
                  onClick={handleMenuClose}
                >
                  <InfoIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common:about', 'About')}
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                  <InfoIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('test:autoTranslate', 'Testing automatic translation with DeepL!')}
                </MenuItem>
                <Divider />
                {isAuthenticated ? (
                  <>
                    <MenuItem
                      component={RouterLink}
                      to="/dashboard"
                      onClick={handleMenuClose}
                    >
                      <DashboardIcon fontSize="small" sx={{ mr: 1 }} />
                      Dashboard
                    </MenuItem>
                    <MenuItem
                      component={RouterLink}
                      to="/settings"
                      onClick={handleMenuClose}
                    >
                      <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                      {t('common:settings', 'Settings')}
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                      {t('common:logout', 'Logout')}
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem
                      component={RouterLink}
                      to="/login"
                      onClick={handleMenuClose}
                    >
                      <LoginIcon fontSize="small" sx={{ mr: 1 }} />
                      Login
                    </MenuItem>
                    <MenuItem
                      component={RouterLink}
                      to="/register"
                      onClick={handleMenuClose}
                    >
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      Register
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex' }}>
              <Button
                color="inherit"
                startIcon={<ForumIcon />}
                endIcon={<ArrowDropDownIcon />}
                sx={{ mr: 1 }}
                onClick={handleDebatesMenuOpen}
              >
                {t('common:debates', 'Debates')}
              </Button>
              <Menu
                anchorEl={debatesMenuAnchorEl}
                open={Boolean(debatesMenuAnchorEl)}
                onClose={handleDebatesMenuClose}
              >
                <EmailVerificationGuard action="create a debate">
                  <MenuItem
                    component={RouterLink}
                    to="/debates/new"
                    onClick={handleDebatesMenuClose}
                  >
                    <CreateIcon fontSize="small" sx={{ mr: 1 }} />
                    {t('common:newDebate')}
                  </MenuItem>
                </EmailVerificationGuard>
                <MenuItem
                  component={RouterLink}
                  to="/home"
                  onClick={handleDebatesMenuClose}
                >
                  <ForumIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common:yourDebates')}
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/debates/public"
                  onClick={handleDebatesMenuClose}
                >
                  <PublicIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common:publicDebates', 'Public Debates')}
                </MenuItem>
              </Menu>
              <Button
                component={RouterLink}
                to="/cube"
                color="inherit"
                startIcon={<CubeIcon />}
                sx={{ mr: 1 }}
              >
                {t('common:cube', 'CUBE')}
              </Button>
              <Button
                component={RouterLink}
                to="/leaderboards"
                color="inherit"
                startIcon={<TrophyIcon />}
                sx={{ mr: 1 }}
              >
                {t('common:leaderboards', 'Leaderboards')}
              </Button>
              <Button
                component={RouterLink}
                to="/rules"
                color="inherit"
                startIcon={<RuleIcon />}
                sx={{ mr: 1 }}
              >
                {t('common:rules', 'Rules')}
              </Button>
              <Button
                component={RouterLink}
                to="/about"
                color="inherit"
                startIcon={<InfoIcon />}
                sx={{ mr: 1 }}
              >
                {t('common:about', 'About')}
              </Button>
              
              {isAuthenticated ? (
                <>
                  {/* Add LanguageSwitcher for logged-in users too */}
                  <LanguageSwitcher variant="icon" />
                  <IconButton onClick={handleUserMenuOpen} color="inherit">
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        backgroundColor: 'primary.main',
                        fontSize: '0.875rem'
                      }}
                    >
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={userMenuAnchorEl}
                    open={Boolean(userMenuAnchorEl)}
                    onClose={handleUserMenuClose}
                  >
                    <MenuItem dense disabled>
                      <Typography variant="body2">
                        Signed in as <strong>{user?.username}</strong>
                      </Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem 
                      component={RouterLink}
                      to="/dashboard"
                      onClick={handleUserMenuClose}
                    >
                      <DashboardIcon fontSize="small" sx={{ mr: 1 }} />
                      Dashboard
                    </MenuItem>
                    <MenuItem 
                      component={RouterLink}
                      to="/settings"
                      onClick={handleUserMenuClose}
                    >
                      <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                      {t('common:settings', 'Settings')}
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                      {t('common:logout', 'Logout')}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  {/* Debug: Try to render LanguageSwitcher */}
                  <LanguageSwitcher variant="icon" />
                  <Button
                    component={RouterLink}
                    to="/login"
                    color="inherit"
                    startIcon={<LoginIcon />}
                    sx={{ mr: 1 }}
                  >
                    {t('common:login')}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/register"
                    color="primary"
                    variant="contained"
                  >
                    {t('common:register')}
                  </Button>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header; 